import PropTypes from 'prop-types';
import React from 'react';
import { DayPicker, HeadRow, InternalModifier, } from 'react-day-picker-v8';
import { supportedWixlocales } from 'wix-design-systems-locale-utils';
import localeUtilsFactory from '../../common/LocaleUtils/LocaleUtils';
import { WixStyleReactEnvironmentContext, } from '../../WixStyleReactEnvironmentProvider/context';
import { CustomHead } from './CustomHead';
import { classes, cssStates, st } from './WsrBaseCalendar.st.css';
// https://classic.yarnpkg.com/en/docs/cli/add/#toc-yarn-add-alias
const getIsRange = (value) => {
    if (!value || value instanceof Date) {
        return false;
    }
    return true;
};
const getCustomModifiers = (props, localeUtils) => {
    const { displayedMonth, modifiers: customModifiers, today, value } = props;
    const range = getIsRange(value) ? value : {};
    const { from, to } = range;
    const singleDay = !getIsRange(value) && value;
    const firstOfWeek = [
        (date) => date.getDay() === localeUtils.getFirstDayOfWeek(),
    ];
    const lastOWeek = [
        (date) => date.getDay() === localeUtils.getLastDayOfWeek(),
    ];
    const firstOfMonth = [
        new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1),
        new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 1),
    ];
    const lastOfMonth = [
        new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 0),
        new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 2, 0),
    ];
    const modifiers = {
        [CustomModifier().start]: [from],
        [CustomModifier().end]: [to],
        [CustomModifier().firstOfWeak]: firstOfWeek,
        [CustomModifier().lastOfWeek]: lastOWeek,
        [CustomModifier().firstOfMonth]: firstOfMonth,
        [CustomModifier().lastOfMonth]: lastOfMonth,
        [CustomModifier().singleDay]: [singleDay],
        [CustomModifier().past]: [{ before: BaseCalendar.parseValue(today) }],
        [CustomModifier().today]: [BaseCalendar.parseValue(today)],
        ...customModifiers,
    };
    return modifiers;
};
const CustomModifier = () => ({
    start: cssStates({ start: true }),
    end: cssStates({ end: true }),
    firstOfWeak: cssStates({ firstOfWeek: true }),
    lastOfWeek: cssStates({ lastOfWeek: true }),
    firstOfMonth: cssStates({ firstOfMonth: true }),
    lastOfMonth: cssStates({ lastOfMonth: true }),
    singleDay: cssStates({ singleDay: true }),
    past: cssStates({ past: true }),
    /** should logically be mutually exclusive with InternalModifier.today / day_today */
    today: cssStates({ today: true }),
});
const customModifierClassNames = () => Object.fromEntries(Object.values(CustomModifier()).map((value) => [value, value]));
class BaseCalendar extends React.PureComponent {
    constructor() {
        super(...arguments);
        this._preventActionEventDefault = (event = null) => {
            if (event &&
                (!event.key || (event.key !== 'Escape' && event.key !== 'Tab'))) {
                event.persist(); // TODO: JB: need for test to pass, but not sure why it was not needed before
                event.preventDefault();
            }
        };
        this.DayContentComponent = (props) => {
            const { date: day, activeModifiers: modifiers } = props;
            const { dateIndication } = this.props;
            const isOutsideDay = !!modifiers[InternalModifier.Outside];
            const isDisabled = !!modifiers[InternalModifier.Disabled];
            const isSelectedDay = !!modifiers[InternalModifier.Selected];
            const dateIndicationNode = dateIndication?.({
                date: day,
                isSelected: isSelectedDay,
            });
            const hasIndication = dateIndicationNode && !isOutsideDay;
            const localeUtils = this._getLocaleUtils();
            return (React.createElement("div", { className: st(classes.dayWrapper, { hasIndication }), "data-date": `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`, "data-outsideday": isOutsideDay, "aria-disabled": isOutsideDay || isDisabled, "aria-label": localeUtils.formatDay(day, undefined) },
                React.createElement("div", { className: classes.dayText, "aria-hidden": true }, day.getDate()),
                hasIndication ? (React.createElement("div", { className: classes.dayIndicationContainer }, dateIndicationNode)) : null));
        };
        this._handleKeyDown = (event) => {
            event.persist(); // TODO: JB: need for test to pass, but not sure why it was not needed before
            const { onKeyDown } = this.props;
            if (onKeyDown) {
                onKeyDown(event);
                return;
            }
            if (event.key === 'Escape') {
                this.props.onClose(event);
            }
        };
        this._handleDayKeyDown = (_value, _modifiers, event = null) => {
            this._preventActionEventDefault(event);
            if (event.key === 'Enter' || event.key === 'Space') {
                this._handleDayClick(_value, _modifiers, null);
            }
        };
        this._handleDayClick = (value, modifiers = { outside: undefined, today: undefined }, event = null) => {
            this._preventActionEventDefault(event);
            const { onChange, shouldCloseOnSelect } = this.props;
            if (this.props.selectionMode === 'range') {
                const propsValue = this.props.value || {};
                if (
                /* both undefined and type safe */ !getIsRange(propsValue) ||
                    /* both undefined */ (!propsValue.from && !propsValue.to) ||
                    /* both defined */ (propsValue.from && propsValue.to)) {
                    onChange({ from: value }, modifiers);
                }
                else {
                    const anchor = propsValue.from || propsValue.to;
                    const newVal = anchor < value
                        ? { from: anchor, to: value }
                        : { from: value, to: anchor };
                    onChange(newVal, modifiers);
                    shouldCloseOnSelect && this.props.onClose(event);
                }
            }
            else {
                onChange(value, modifiers);
                shouldCloseOnSelect && this.props.onClose(event);
            }
        };
        this._createDayPickerProps = () => {
            const { filterDate: isDateEnabled, excludePastDates, numOfMonths, rtl, onDisplayedViewChange, displayedMonth, captionComponent: CaptionComponent, allowSelectingOutsideDays, size, fixedWeeks, } = this.props;
            const value = BaseCalendar.parseValue(this.props.value);
            const localeUtils = this._getLocaleUtils();
            const selectedDays = this._getSelectedDays(value);
            const modifiers = getCustomModifiers(this.props, localeUtils);
            const modifiersClassNames = {
                ...customModifierClassNames(),
            };
            const dayPickerProps = {
                classNames: {
                    // all buttons reset (day and previous/next)
                    button_reset: classes.button_reset,
                    /* The classes: 'DayPicker', 'DayPicker-wrapper', 'DayPicker-Month', 'DayPicker-Day', 'disabled'
                    are used as selectors for the elements at the drivers and at the e2e tests */
                    root: st(classes.container, { size }, 'DayPicker'),
                    months: st(classes.months, { twoMonths: numOfMonths > 1 }, 'DayPicker-wrapper'),
                    month: st(classes.month, { size }, 'DayPicker-Month'),
                    table: st(classes.table, { size }),
                    head: classes.weekdays,
                    head_row: classes.weekdaysRow,
                    head_cell: st(classes.weekday, { size }),
                    tbody: st(classes.body, { size }),
                    row: classes.week,
                    day: st(classes.day, { size }, 'DayPicker-Day'),
                    // default modifiers
                    day_today: modifiers[CustomModifier().today].length === 0
                        ? modifiersClassNames[CustomModifier().today]
                        : undefined,
                    day_selected: st(cssStates({ selected: true })),
                    day_disabled: st(cssStates({ disabled: true }), 'disabled'), // disabled natively, fully
                    day_outside: st(cssStates({ outside: !allowSelectingOutsideDays })), // disabled only visually, not mouse clickable but clickable programmatically, not navigable by keyboard because month changes
                },
                components: {
                    Caption: CaptionComponent,
                    DayContent: this.DayContentComponent,
                    Head: CustomHead,
                    HeadRow,
                },
                defaultMonth: displayedMonth,
                dir: rtl ? 'rtl' : 'ltr',
                disabled: new Array((date) => !isDateEnabled(new Date(date)), excludePastDates ? { before: new Date() } : undefined).filter(Boolean),
                fixedWeeks,
                formatters: {
                    formatWeekdayName: (date, _options) => localeUtils.formatWeekdayShort(date.getDay(), undefined),
                },
                initialFocus: this.props.autoFocus,
                /**
                 * must be object of type Locale from date-fns
                 * but {@link BaseCalendarProps.locale} is of type {@link supportedWixlocales}
                 * our locale must be adapted to locale of react-day-picker or {@link dayPickerProps.formatters}, {@link dayPickerProps.labels}, {@link dayPickerProps.weekStartsOn} props must be used
                 */
                locale: undefined,
                labels: {
                    labelWeekday: (_day, _options) => undefined,
                },
                mode: undefined,
                modifiers,
                modifiersClassNames,
                month: displayedMonth,
                numberOfMonths: numOfMonths,
                onDayClick: this._handleDayClick,
                onDayKeyDown: this._handleDayKeyDown,
                onMonthChange: onDisplayedViewChange,
                selected: selectedDays,
                showOutsideDays: true,
                weekStartsOn: localeUtils.getFirstDayOfWeek(),
            };
            return dayPickerProps;
        };
    }
    static parseValue(value) {
        if (!value) {
            return new Date();
        }
        if (value instanceof Date) {
            return value;
        }
        else {
            return {
                from: value.from,
                to: value.to,
            };
        }
    }
    _getSelectedDays(value) {
        const range = getIsRange(value) ? value : {};
        const { from, to } = range;
        if (from && to) {
            return { from, to };
        }
        else if (from) {
            return { after: BaseCalendar.prevDay(from) };
        }
        else if (to) {
            return { before: BaseCalendar.nextDay(to) };
        }
        else {
            // Single day OR empty value
            return value;
        }
    }
    _getLocaleOld() {
        return this.props.locale || this.context.locale || 'en';
    }
    _getLocaleUtils() {
        const { firstDayOfWeek } = this.props;
        const localeOld = this._getLocaleOld();
        const localeUtils = localeUtilsFactory(localeOld, firstDayOfWeek);
        return localeUtils;
    }
    render() {
        const { dataHook, className, size, id } = this.props;
        return (React.createElement("div", { "data-hook": dataHook, "data-size": size, className: st(classes.root, className), onClick: this._preventActionEventDefault, onKeyDown: this._handleKeyDown, id: id, role: "dialog", tabIndex: -1, "aria-modal": true },
            React.createElement(DayPicker, { ...this._createDayPickerProps() })));
    }
}
BaseCalendar.displayName = 'BaseCalendar';
BaseCalendar.defaultProps = {
    className: '',
    filterDate: () => true,
    dateIndication: () => null,
    shouldCloseOnSelect: true,
    onClose: () => { },
    autoFocus: true,
    excludePastDates: false,
    selectionMode: 'day',
    // @ts-expect-error
    showMonthDropdown: false,
    showYearDropdown: false,
    numOfMonths: 1,
    size: 'medium',
    allowSelectingOutsideDays: false,
    fixedWeeks: true,
};
BaseCalendar.nextDay = (date) => {
    const day = new Date(date);
    day.setDate(day.getDate() + 1);
    return day;
};
BaseCalendar.prevDay = (date) => {
    const day = new Date(date);
    day.setDate(day.getDate() - 1);
    return day;
};
export default BaseCalendar;
BaseCalendar.contextType = WixStyleReactEnvironmentContext;
BaseCalendar.propTypes = {
    dataHook: PropTypes.string,
    autoFocus: PropTypes.bool,
    numOfMonths: PropTypes.oneOf([1, 2]),
    firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
    className: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    onKeyDown: PropTypes.func,
    excludePastDates: PropTypes.bool,
    filterDate: PropTypes.func,
    value: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.shape({
            from: PropTypes.instanceOf(Date),
            to: PropTypes.instanceOf(Date),
        }),
    ]),
    selectionMode: PropTypes.oneOf(['day', 'range']),
    shouldCloseOnSelect: PropTypes.bool,
    locale: PropTypes.oneOfType([PropTypes.oneOf(supportedWixlocales)]),
    rtl: PropTypes.bool,
    dateIndication: PropTypes.func,
    today: PropTypes.instanceOf(Date),
    displayedMonth: PropTypes.instanceOf(Date).isRequired,
    onDisplayedViewChange: PropTypes.func.isRequired,
    // @ts-expect-error TODO: JB: propTypes
    captionComponent: PropTypes.elementType.isRequired,
    modifiers: PropTypes.object,
    allowSelectingOutsideDays: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium']),
};
//# sourceMappingURL=BaseCalendar.js.map